<template>
  <SubscribeForm
    v-model:email-address="emailAddress"
    v-model:metadata="metadata"
    v-model:turnstile-response-token="turnstileResponseToken"
    v-model:survey-responses="surveyResponses"
    :newsletter="newsletter"
    :subscriber="subscriber"
    :check-subscriber="checkSubscriber"
    :subscribe="subscribe"
    :resubscribe="resubscribe"
    :state="state"
    :variant="variant"
    :error="error"
    :surveys="surveys"
  />
</template>

<script setup lang="ts">
import { ref, watch, watchEffect } from "vue";

import Urls from "@/autogen/urls";
import type { Newsletter } from "@/types/newsletter";

import {
  type BasicSubscriber,
  type FormSurvey,
  type SubscribeFormVariant,
  type SubscriberFacingAuthentication,
  type SubscriptionState,
  parseError,
  subscribe as subscribeApi,
} from "./lib";
import SubscribeForm from "./SubscribeForm.vue";

const calculateSubscriptionState = (
  subscriber: BasicSubscriber | undefined,
  authenticationStatus: SubscriberFacingAuthentication | undefined
): SubscriptionState => {
  if (subscriber === null || subscriber === undefined) {
    if (authenticationStatus?.status !== "unauthenticated") {
      return "subscribed_elsewhere";
    }
    return "not_subscribed";
  }
  return subscriber.type;
};

declare var REFERRING_SUBSCRIBER_ID: string;

interface Props {
  newsletter: Newsletter;
  subscriberFacingAuthentication?: SubscriberFacingAuthentication;
  variant: SubscribeFormVariant;
  onSubscribe?: (subscriber: BasicSubscriber) => void;
  surveys?: FormSurvey[];
  formId?: string | null;
}

const props = defineProps<Props>();

const state = ref<SubscriptionState>("not_subscribed");
const emailAddress = ref(
  props.subscriberFacingAuthentication?.email_address || ""
);
const metadata = ref<{ [key: string]: string }>({});
const surveyResponses = ref<Record<string, string | number | number[]>>({});
const turnstileResponseToken = ref("");
const subscriber = ref<BasicSubscriber | undefined>(
  props.subscriberFacingAuthentication?.subscribers?.find(
    (subscriber) => subscriber.newsletter_id === props.newsletter.id
  ) || undefined
);
const error = ref("");

watchEffect(() => {
  state.value = calculateSubscriptionState(
    subscriber.value,
    props.subscriberFacingAuthentication
  );
});

watch(subscriber, () => {
  state.value = calculateSubscriptionState(
    subscriber.value,
    props.subscriberFacingAuthentication
  );
  if (subscriber.value) {
    emailAddress.value = subscriber.value.email_address;
  }
});

// Turnstile is only registered for Buttondown's own domains, not custom domains.
const isOnCustomDomain =
  props.newsletter.domain !== "" &&
  window.location.hostname === props.newsletter.domain;

// Auto-submit when Turnstile token is received after an invalid_ip_address challenge
watch(turnstileResponseToken, (newToken) => {
  if (newToken && state.value === "invalid_ip_address") {
    subscribe();
  }
});

const submitSurveyResponses = async (subscriberId: string) => {
  const surveyUrl = `${window.location.pathname.replace(
    /\/$/,
    ""
  )}/survey-responses`;
  try {
    await fetch(surveyUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        subscriber_id: subscriberId,
        responses: surveyResponses.value,
      }),
    });
  } catch (e) {
    console.error("Failed to submit survey responses", e);
  }
};

const subscribe = async () => {
  state.value = "pending_subscription";
  const result = await subscribeApi(
    emailAddress.value,
    metadata.value,
    props.newsletter,
    REFERRING_SUBSCRIBER_ID,
    turnstileResponseToken.value
  );
  if (!("error" in result)) {
    subscriber.value = result.subscriber;
    state.value = calculateSubscriptionState(
      result.subscriber,
      props.subscriberFacingAuthentication
    );
    // Clear metadata after successful subscription (especially important for embed mode)
    metadata.value = {};
    // Submit survey responses if any
    if (props.formId && Object.keys(surveyResponses.value).length > 0) {
      submitSurveyResponses(result.subscriber.id);
    }
    // Notify parent of successful subscription
    if (props.onSubscribe) {
      props.onSubscribe(result.subscriber);
    }
    if (props.newsletter.subscription_redirect_url) {
      window.location.href = props.newsletter.subscription_redirect_url;
    }
  } else {
    // If the error includes subscriber data (e.g., "Email already exists"),
    // use it so the UI shows the correct subscription state
    if (result.data?.subscriber) {
      subscriber.value = {
        id: result.data.subscriber.id as string,
        email_address: result.data.subscriber.email_address as string,
        type: result.data.subscriber.type as SubscriptionState,
        newsletter_id: props.newsletter.id,
      };
    } else {
      const { state: newState, error: newError } = parseError(
        result.error,
        result.data
      );
      // On custom domains Turnstile is unavailable, so don't offer a CAPTCHA challenge
      if (isOnCustomDomain && newState === "invalid_ip_address") {
        state.value = "errored_email";
        error.value =
          "We're unable to process your subscription at this time. Please try again later.";
      } else {
        state.value = newState;
        error.value = newError;
      }
    }
  }
};

const resubscribe = async (subscriber_id: string) => {
  state.value = "pending_subscription";

  // For a while, we had a comment saying:
  // > This implementation, I think, is technically insufficient; if a subscriber
  // > has a paused/churned subscription it should be set to that instead of the mere 'unpaid'.
  // > But that's a relatively complicated bit of logic to ship to the frontend, so I think
  // > this is sufficient.
  // It's still too much to submit to the frontend so we're just going to override
  // the type on the backend if such a case is detected. (This is not part of the public API,
  // so I don't worry that bad about abstraction leak or the general jank level here.)
  const type = props.newsletter.should_disable_non_premium_subscriptions
    ? "unpaid"
    : "regular";
  const response = await fetch(Urls["api:retrieve_subscriber"](subscriber_id), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ type }),
  });
  const subscriberData = (await response.json()).subscriber;

  subscriber.value = {
    id: subscriberData.id as string,
    email_address: subscriberData.email_address as string,
    type: subscriberData.type as SubscriptionState,
    newsletter_id: props.newsletter.id,
  };
};

// Client-side email validation matching the API's regex pattern
const isValidEmail = (email: string): boolean => {
  if (!email || !email.includes("@")) {
    return false;
  }
  if (email.length > 254) {
    return false;
  }
  const emailRegex = /^[a-zA-Z0-9.'_%+\-!]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

// No-op function to satisfy the prop interface. The sign-in link polling in
// SubscribeForm.vue calls this, but validation now happens at submission time.
const checkSubscriber = () => {
  // Client-side validation only - server validation happens on submit
  if (!emailAddress.value) {
    return;
  }
  if (!isValidEmail(emailAddress.value)) {
    state.value = "invalid_email__regex";
    error.value = "That email address is invalid";
    return;
  }
  // If we have a subscriber already, keep their state
  if (subscriber.value) {
    return;
  }
  // Otherwise, ready to subscribe
  state.value = "not_subscribed";
};
</script>
